import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Sparkles, CheckCircle2, Ticket } from "lucide-react";

export default function BookingForm() {
  const [source, setSource] = useState("Agoda");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Harus PDF");
    if (file.size > 10 * 1024 * 1024) return toast.error("Maks 10 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/bookings/upload-pdf?source=${encodeURIComponent(source)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.duplicate) {
        toast.warning(`⚠️ PNR ${data.duplicate.pnr} sudah pernah dibuat sebagai ${data.duplicate.eticket_number} (${data.duplicate.passenger}) — cek dulu sebelum kirim ke customer`, { duration: 10000 });
      } else {
        toast.success(`Booking & E-ticket ${data.eticket.eticket_number} berhasil dibuat`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal upload/parse PDF");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="booking-form">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Retrieve Booking OTA</h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Upload PDF e-ticket dari OTA (Agoda/Trip.com/Traveloka/Tiket.com). Sistem akan otomatis membuat booking record + e-ticket Gogojalan.</p>
      </div>

      <Card className="border-[#e4ecc9]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sparkles size={18} className="text-[#6b8e23]"/> Upload PDF OTA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Sumber (OTA)</label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Agoda","Trip.com","Traveloka","Tiket.com","Airline Direct","Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <input ref={fileRef} type="file" accept="application/pdf" onChange={onUpload} className="hidden" data-testid="booking-pdf-upload"/>
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full bg-[#556b2f] hover:bg-[#3d4d1e] h-11">
                <Upload size={16} className="mr-2"/> {uploading ? "Memproses..." : "Pilih PDF & Retrieve"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-400">Multi-segment & multi-penumpang didukung. Data booking + e-ticket Gogojalan dibuat sekaligus.</p>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-[#e4ecc9]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#556b2f]">
              <CheckCircle2 size={18}/> Berhasil dibuat
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[#f0f2e6] border border-[#e4ecc9]">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Booking OTA</div>
                <div className="font-bold text-lg mt-1 mono">{result.booking.booking_number || "-"}</div>
                <div className="text-sm text-slate-600 mt-2">{result.booking.airline} · {result.booking.passenger}</div>
                <div className="text-xs text-slate-500 mt-1">{result.booking.route} · PNR {result.booking.pnr}</div>
                <span className="pill pill-issued mt-3">Status: {result.booking.status}</span>
              </div>
              <div className="p-4 rounded-lg bg-[#f0f2e6] border border-[#e4ecc9]">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">E-ticket Gogojalan</div>
                <div className="font-bold text-lg mt-1 mono">{result.eticket.eticket_number}</div>
                <div className="text-sm text-slate-600 mt-2">{(result.eticket.passengers || []).map(p => p.name).join(", ")}</div>
                <div className="text-xs text-slate-500 mt-1">{(result.eticket.flights || []).length} segmen</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => nav("/bookings")}>Ke daftar Booking</Button>
              <Button className="bg-[#556b2f] hover:bg-[#3d4d1e]" onClick={() => nav("/etickets")}><Ticket size={14} className="mr-1"/> Ke daftar E-ticket</Button>
              <Button variant="ghost" onClick={() => setResult(null)}>Upload lagi</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
